import { useState } from "react";
import { useWizard } from "../CalendarForm";
import {
  ButtonGroup,
  CardSelector,
  ToggleSwitch,
  SectionHeader,
  SummaryCard,
} from "../components/inputs";

// Icons
const MetabolicIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

// Configuration options
const CYCLING_TYPE_OPTIONS = [
  { id: "carb-cycling", label: "Carb Cycling", description: "Alternate high/low carb days", icon: "🔄" },
  { id: "calorie-cycling", label: "Calorie Cycling", description: "Surplus on training, deficit on rest", icon: "📊" },
  { id: "maintenance", label: "Maintenance", description: "Steady calories, recomp approach", icon: "⚖️" },
];

const INTENSITY_OPTIONS = [
  { id: "low", label: "Low" },
  { id: "moderate", label: "Moderate" },
  { id: "high", label: "High" },
  { id: "intense", label: "Intense" },
];

const METABOLIC_SESSIONS_OPTIONS = [
  { id: 2, label: "2x" },
  { id: 3, label: "3x" },
  { id: 4, label: "4x" },
  { id: 5, label: "5x" },
];

const REST_DAYS_OPTIONS = [
  { id: 1, label: "1 day" },
  { id: 2, label: "2 days" },
  { id: 3, label: "3 days" },
];

/**
 * MetabolicBuildingConfiguration Component
 * 
 * Step 2 configuration for body recomposition.
 */
export default function MetabolicBuildingConfiguration() {
  const { formData, updateFormData } = useWizard();

  const [config, setConfig] = useState({
    cyclingType: formData.metabolicConfig?.cyclingType || "carb-cycling",
    trainingIntensity: formData.metabolicConfig?.trainingIntensity || "moderate",
    metabolicSessions: formData.metabolicConfig?.metabolicSessions || 3,
    restDays: formData.metabolicConfig?.restDays || 2,
    trackBiomarkers: formData.metabolicConfig?.trackBiomarkers ?? true,
  });

  const handleChange = (field, value) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    updateFormData({ metabolicConfig: newConfig });
  };

  const strengthDays = Math.max(0, 7 - config.metabolicSessions - config.restDays);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Metabolic Building Configuration"
        description="Optimize for body recomposition"
        icon={MetabolicIcon}
        color="amber"
      />

      <CardSelector
        label="Nutrition Cycling Strategy"
        value={config.cyclingType}
        onChange={(value) => handleChange("cyclingType", value)}
        options={CYCLING_TYPE_OPTIONS}
        color="amber"
        columns={3}
      />

      <ButtonGroup
        label="Training Intensity Level"
        value={config.trainingIntensity}
        onChange={(value) => handleChange("trainingIntensity", value)}
        options={INTENSITY_OPTIONS}
        color="amber"
      />

      <ButtonGroup
        label="Weekly Metabolic Sessions"
        description="Circuits, complexes, and conditioning work"
        value={config.metabolicSessions}
        onChange={(value) => handleChange("metabolicSessions", value)}
        options={METABOLIC_SESSIONS_OPTIONS}
        color="amber"
      />

      <ButtonGroup
        label="Active Rest Days per Week"
        description="Light activity for recovery"
        value={config.restDays}
        onChange={(value) => handleChange("restDays", value)}
        options={REST_DAYS_OPTIONS}
        color="amber"
      />

      <ToggleSwitch
        label="Track Biomarkers"
        description="Monitor energy, sleep, HRV, and recovery"
        checked={config.trackBiomarkers}
        onChange={(value) => handleChange("trackBiomarkers", value)}
        color="amber"
      />

      <SummaryCard title="Weekly Overview" color="amber">
        <SummaryCard.StatGrid columns={3}>
          <SummaryCard.Stat value={config.metabolicSessions} label="Metabolic" />
          <SummaryCard.Stat value={strengthDays} label="Strength" />
          <SummaryCard.Stat value={config.restDays} label="Rest" />
        </SummaryCard.StatGrid>
      </SummaryCard>
    </div>
  );
}